import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngxs/store';
import { ZefThemePalette } from '@shared/models/color.model';
import { HelpCenterSidenav } from '@shared/modules/help-center/components/help-sidenav/help-sidenav.component';
import {
  HelpCenterItemStatus,
  HelpCenterLanguage,
  HelpCenterMode,
  HelpItemSubject,
} from '@shared/modules/help-center/help-center.models';
import {
  GetHelpItemStatistics,
  GetHelpItemStatus,
  GetHelpItemTip,
  GetHelpItemTitle,
  HideArticle,
  ShowArticle,
  UpdateHelpCenterMode,
} from '@shared/modules/help-center/state/help-center.actions';
import { HelpCenterState } from '@shared/modules/help-center/state/help-center.state';
import { getLastValue, shareRef } from '@shared/operators/share-ref.operator';
import { AccountState } from '@shared/states/account.state';
import { AuthState } from '@shared/states/auth.state';
import { OpenSidenav } from '@shared/states/dialog.actions';
import { PrefsState } from '@shared/states/prefs.state';
import { assertStoreData } from '@shared/utilities/store.utilities';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';
import { MatMenu } from '@angular/material/menu';
import { SidenavRef } from '@shared/modules/sidenav/models/sidenav.models';
import { TooltipPosition } from '@angular/material/tooltip';

@Component({
  selector: 'zef-help-icon',
  templateUrl: './help-icon.component.html',
  styleUrls: ['./help-icon.component.scss'],
  providers: [LifecycleHooks],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HelpIcon implements OnInit, OnDestroy {
  private store = inject(Store);
  private lh = inject(LifecycleHooks);
  private sn = inject(SidenavRef, { optional: true });
  private mn = inject(MatMenu, { optional: true });

  readonly statusColorMap: Record<HelpCenterItemStatus, string> = {
    [HelpCenterItemStatus.Missing]: 'alert',
    [HelpCenterItemStatus.Partial]: 'warning',
    [HelpCenterItemStatus.Complete]: 'ink-lightest',
  };

  readonly _subject$ = new ReplaySubject<HelpItemSubject>(1);

  static readonly Cache$: Partial<
    Record<
      HelpItemSubject,
      Partial<{
        showCount: number;
        status: Observable<HelpCenterItemStatus>;
        stats: Observable<unknown>;
        [key: `title-${string}`]: Observable<unknown>;
        [key: `tip-${string}`]: Observable<string>;
      }>
    >
  > = {};

  readonly lang$ = this.store.select(PrefsState.language) as Observable<HelpCenterLanguage>;

  @Input()
  position: TooltipPosition = 'above';

  @Input()
  set subject(subject: HelpItemSubject) {
    this._subject$.next(subject);
  }

  readonly shownArticles = select(HelpCenterState.shownArticles);

  readonly subject$: Observable<HelpItemSubject> = this._subject$.pipe(
    filter((subject) => !!subject),
    distinctUntilChanged(),
    shareRef(),
  );

  readonly status$: Observable<HelpCenterItemStatus> = this.subject$.pipe(
    switchMap((subject) =>
      this.assertCacheItem(subject, 'status', () =>
        assertStoreData(this.store, HelpCenterState.status(subject), new GetHelpItemStatus(subject)),
      ),
    ),
    shareRef(),
  );

  readonly color$: Observable<ZefThemePalette> = this.status$.pipe(
    map((status) => this.statusColorMap[status] as ZefThemePalette),
  );

  readonly tip$: Observable<string> = combineLatest([this.subject$, this.lang$]).pipe(
    switchMap(([subject, lang]) =>
      this.assertCacheItem(subject, `tip-${lang}`, () =>
        assertStoreData(this.store, HelpCenterState.tip(subject, lang), new GetHelpItemTip(subject, lang)),
      ),
    ),
    shareRef(),
  );

  readonly canEdit$ = combineLatest([
    this.store.select(AuthState.isZefAdmin),
    this.store.select(AccountState.isParentAdmin),
  ]).pipe(
    map(([isAdmin, isParentAdmin]) => isAdmin || isParentAdmin),
    shareRef(),
  );

  readonly show$: Observable<boolean> = combineLatest([this.status$, this.canEdit$]).pipe(
    map(([status, canEdit]) => status === HelpCenterItemStatus.Complete || canEdit),
  );

  ngOnInit() {
    if (!this.sn) {
      if (!this.mn) {
        const subject = getLastValue(this.subject$);
        this.assertCacheItem(subject, 'showCount', () => 0);
        if (!HelpIcon.Cache$[subject].showCount) {
          HelpIcon.Cache$[subject].showCount++;
        }

        if (!this.shownArticles().includes(subject)) {
          const lang = this.store.selectSnapshot(PrefsState.language) as HelpCenterLanguage;
          this.store.dispatch(new ShowArticle(subject));

          this.assertCacheItem(subject, `title-${lang}`, () =>
            assertStoreData(
              this.store,
              HelpCenterState.data(subject, lang),
              new GetHelpItemTitle(subject, lang),
              (data) => data?.title != null,
            ),
          )
            .pipe(takeUntil(this.lh.destroy))
            .subscribe();

          this.assertCacheItem(subject, 'stats', () =>
            assertStoreData(
              this.store,
              HelpCenterState.stats(subject),
              new GetHelpItemStatistics(subject),
              (data) => data?.stats != null,
            ),
          )
            .pipe(takeUntil(this.lh.destroy))
            .subscribe();
        }
      }
    }
  }

  ngOnDestroy() {
    if (!this.sn && !this.mn) {
      const subject = getLastValue(this.subject$);
      if (HelpIcon.Cache$[subject]) {
        const count = --HelpIcon.Cache$[subject].showCount;

        if (!count) {
          this.store.dispatch(new HideArticle(subject));
        }
      }
    }
  }

  openArticle(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const subject = getLastValue(this.subject$);
    const status = getLastValue(this.status$);
    const canEdit = getLastValue(this.canEdit$);

    this.store.dispatch([
      new UpdateHelpCenterMode({
        mode: canEdit && status !== HelpCenterItemStatus.Complete ? HelpCenterMode.Edit : HelpCenterMode.Read,
        language: getLastValue(this.lang$),
      }),
      new OpenSidenav(HelpCenterSidenav, { data: { subject } }),
    ]);
  }

  private assertCacheItem<T>(subject: HelpItemSubject, cachePath: string, itemFn: () => T): T {
    const subjectCache = HelpIcon.Cache$[subject];

    if (!subjectCache) {
      HelpIcon.Cache$[subject] = {};
    }

    const $ = HelpIcon.Cache$[subject][cachePath];

    if (!$) {
      HelpIcon.Cache$[subject][cachePath] = itemFn();
    }

    return HelpIcon.Cache$[subject][cachePath];
  }
}
